import React, { useRef, MutableRefObject, useCallback } from 'react';
import { useOnClickOutside } from 'usehooks-ts';
import ReactMarkdown from 'react-markdown';
import { CLICKABLE_WORD } from '../ClickableWord';
import { ToolWrapper, StyledControlButton, StyledParentControlWrapper } from '../Styled';
import {
  getLanguageFeatureLabels,
  getColorByPartOfSpeech,
  getLanguageFeatureTextContent,
  getLanguageFeatureButtonBorder,
  isLFControlButtonClicked,
} from '../../utils';
import { LanguageFeatureLabel, ExemplarWord } from '../../types';
import { ITool } from '../../contexts/styledWordContext';
import { TooltipForLanguageWordPointer } from '../Styled/StyledTooltipPointer';
import { CoverShadowIcon } from '../../assets/icons/HelpIcon';
import { getNoHighlightStyles, stylesToHighlightLangWord } from '../ClickableWord/ClickableWord';
import { PHRASES, PREPOSITIONAL_PHRASES } from '../../constants';
import { SEPARATE_NEXT, SEPARATE_NEXT_PHRASE } from '../../constants/textEditor';
import { isUndoOrRedoButtonClicked } from '../../utils/undoRedo';

interface ILanguageFeature {
  tool: ITool;
  exemplarWord: ExemplarWord;
  languageFeatureLabels: LanguageFeatureLabel[];
  handleTool: (isVisible: boolean, word: ExemplarWord, x: number, y: number) => void;
  handleFontStyle: (word: ExemplarWord) => void;
  handleLanguage?: (word: ExemplarWord) => void;
  saveWordLanguageUpdates: (prevState: ExemplarWord, state: ExemplarWord) => void;
}

export const LanguageFeatureTool = (props: ILanguageFeature): JSX.Element | null => {
  const {
    handleTool,
    handleFontStyle,
    tool,
    languageFeatureLabels,
    exemplarWord,
    handleLanguage,
    saveWordLanguageUpdates,
  } = props;

  const fontToolRef = useRef() as MutableRefObject<HTMLDivElement>;

  const onOutClick = useCallback(
    event => {
      const isWordClicked = (event.target as HTMLSpanElement).classList.contains(CLICKABLE_WORD);

      if (isWordClicked) {
        event.target.click();
        return;
      }

      const style = getNoHighlightStyles(exemplarWord.style, stylesToHighlightLangWord);

      const word = {
        ...exemplarWord,
        style,
      };

      if (event.target.type !== 'button') {
        handleFontStyle(word);
        handleToolState();
        return;
      }

      const isLFControlClicked = isLFControlButtonClicked(event);

      if (isLFControlClicked) {
        handleFontStyle(word);
        handleToolState();
        (event.target as HTMLButtonElement).dispatchEvent(new Event('mousedown'));
        return;
      }

      const isUndoOrRedoClicked = isUndoOrRedoButtonClicked(event);

      if (isUndoOrRedoClicked) {
        const { language, phrase, prepositional_phrase, ...wordWithoutLanguage } = word;

        event.target.click();
        handleFontStyle(wordWithoutLanguage);
      } else {
        event.target.click();
        handleFontStyle(word);
      }

      handleToolState();
    },
    [exemplarWord]
  );

  const handleToolState = () => {
    return handleTool(
      false,
      {
        side: '',
        text: '',
        id: '',
        wordId: '',
        lineId: '',
        sectionId: '',
        wordIndex: 0,
        lineIndex: 0,
        sectionIndex: 0,
      },
      0,
      0
    );
  };

  useOnClickOutside(fontToolRef, onOutClick);

  const onControlClick = (language: string) => {
    if (!handleLanguage) {
      return;
    }

    const noHighlightStyles = getNoHighlightStyles(exemplarWord.style, stylesToHighlightLangWord);

    if (language === PHRASES) {
      const word = { ...exemplarWord };
      delete word.prepositional_phrase;

      saveWordLanguageUpdates(
        { ...word, style: noHighlightStyles },
        { ...word, style: noHighlightStyles, phrase: !word.phrase }
      );

      return handleLanguage({ ...word, phrase: !word.phrase } as ExemplarWord);
    }

    if (language === PREPOSITIONAL_PHRASES) {
      const word = { ...exemplarWord };
      delete word.phrase;

      saveWordLanguageUpdates(
        { ...word, style: noHighlightStyles },
        { ...word, style: noHighlightStyles, prepositional_phrase: !word.prepositional_phrase }
      );

      return handleLanguage({ ...word, prepositional_phrase: !word.prepositional_phrase } as ExemplarWord);
    }

    let styles = exemplarWord.style || [];

    if (styles.includes(SEPARATE_NEXT) || styles.includes(SEPARATE_NEXT_PHRASE)) {
      styles = styles.filter(style => style !== SEPARATE_NEXT && style !== SEPARATE_NEXT_PHRASE);
    }

    if (exemplarWord.language && exemplarWord.language == language) {
      const { ...word } = exemplarWord;

      saveWordLanguageUpdates(
        { ...word, style: noHighlightStyles },
        { ...word, language: undefined, style: noHighlightStyles }
      );

      return handleLanguage({ ...word, language: undefined, style: styles } as ExemplarWord);
    }

    const word = { ...exemplarWord, language: language, style: styles };

    saveWordLanguageUpdates(
      { ...exemplarWord, style: noHighlightStyles },
      { ...exemplarWord, language: language, style: noHighlightStyles }
    );

    return handleLanguage(word as ExemplarWord);
  };

  const getButtonColor = (label: string) => {
    const { language, prepositional_phrase, phrase } = exemplarWord;
    const color = getColorByPartOfSpeech(label);
    const isPhrases = label === PHRASES;
    const isPrepositionalPhrases = label === PREPOSITIONAL_PHRASES;
    const isSelected = language === label || (isPrepositionalPhrases && prepositional_phrase) || (isPhrases && phrase);

    if (isPrepositionalPhrases && isSelected) {
      return 'transparent';
    }

    return color;
  };

  return (
    <ToolWrapper ref={fontToolRef} x={tool.x} y={tool.y} margin="12px 0px 0px 0px" data-testid="font-tool">
      <TooltipForLanguageWordPointer />
      <CoverShadowIcon />
      <StyledParentControlWrapper>
        {languageFeatureLabels?.map((languageLabel: LanguageFeatureLabel, index: number) => {
          const { label } = languageLabel;
          const name = getLanguageFeatureLabels(label);
          const isPrepositionalPhrases = label === PREPOSITIONAL_PHRASES;
          const isSelected =
            exemplarWord.language === label ||
            (isPrepositionalPhrases && exemplarWord.prepositional_phrase) ||
            (label === PHRASES && exemplarWord.phrase);

          return (
            <StyledControlButton
              key={index}
              testId="control-button"
              borderRadius="4px"
              height="24px"
              fontSize="10px"
              color={getButtonColor(label)}
              withOpacity={isSelected}
              onClick={() => onControlClick(label)}
              border={getLanguageFeatureButtonBorder(label)}
            >
              <ReactMarkdown skipHtml={false}>{getLanguageFeatureTextContent(name)}</ReactMarkdown>
            </StyledControlButton>
          );
        })}
      </StyledParentControlWrapper>
    </ToolWrapper>
  );
};
